import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { addDays, format, getDay } from "date-fns";
import { MinusCircle, PlusCircle } from "lucide-react";
import { useState } from "react";
import { CustomRecurrence } from "./custom-recurrence-utils";

const getNextOccurrence = (startDate: Date, interval: number, unit: string) => {
  const nextDate = new Date(startDate);
  switch (unit) {
    case "day":
      nextDate.setDate(nextDate.getDate() + interval);
      break;
    case "week":
      nextDate.setDate(nextDate.getDate() + interval * 7);
      break;
    case "month":
      nextDate.setMonth(nextDate.getMonth() + interval);
      break;
    case "year":
      nextDate.setFullYear(nextDate.getFullYear() + interval);
      break;
  }
  return nextDate;
};

const CustomRecurrenceDialog = ({
  open,
  onClose,
  customRecurrence,
  setCustomRecurrence,
  startDate,
}: {
  open: boolean;
  onClose: () => void;
  customRecurrence: CustomRecurrence;
  setCustomRecurrence: (customRecurrence: CustomRecurrence) => void;
  startDate: Date;
}) => {
  const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];
  const [selectedDayIndex, setSelectedDayIndex] = useState(getDay(new Date()));
  const [repeatEvery, setRepeatEvery] = useState(customRecurrence.interval);
  const [repeatUnit, setRepeatUnit] = useState(customRecurrence.unit);
  const [endType, setEndType] = useState(customRecurrence.endType);
  const [endDate, setEndDate] = useState(
    customRecurrence.endDate || addDays(new Date(), 1)
  );
  const [occurrences, setOccurrences] = useState(
    customRecurrence.occurrences || 13
  );
  const [selectedDays, setSelectedDays] = useState(
    customRecurrence.weekdays || []
  );

  const incrementRepeatEvery = () => setRepeatEvery((prev) => prev + 1);
  const decrementRepeatEvery = () =>
    setRepeatEvery((prev) => Math.max(1, prev - 1));

  const incrementOccurrences = () => setOccurrences((prev) => prev + 1);
  const decrementOccurrences = () =>
    setOccurrences((prev) => Math.max(1, prev - 1));

  const getOccurrencesText = (value: number) => {
    return `${value} ${value === 1 ? "Occurrence" : "Occurrences"}`;
  };

  const handleDone = () => {
    setCustomRecurrence({
      ...customRecurrence,
      interval: repeatEvery,
      unit: repeatUnit,
      endType,
      endDate: endType === "on" ? endDate : undefined,
      occurrences: endType === "after" ? occurrences : undefined,
      weekdays: repeatUnit === "week" ? selectedDays : undefined,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Custom recurrence</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Repeat every</Label>
            <div className="flex items-center space-x-3 bg-muted p-2 rounded-md">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 rounded-full"
                onClick={decrementRepeatEvery}
              >
                <MinusCircle className="h-4 w-4" />
              </Button>
              <div className="flex-1">
                <Input
                  type="number"
                  value={repeatEvery}
                  onChange={(e) => setRepeatEvery(Number(e.target.value))}
                  className="w-full text-center h-8 bg-background"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 rounded-full"
                onClick={incrementRepeatEvery}
              >
                <PlusCircle className="h-4 w-4" />
              </Button>
              <Select
                value={repeatUnit}
                onValueChange={(value) =>
                  setRepeatUnit(value as "day" | "week" | "month" | "year")
                }
              >
                <SelectTrigger className="w-[120px] h-8 bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Day(s)</SelectItem>
                  <SelectItem value="week">Week(s)</SelectItem>
                  <SelectItem value="month">Month(s)</SelectItem>
                  <SelectItem value="year">Year(s)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Next occurrence:{" "}
              {format(
                getNextOccurrence(startDate, repeatEvery, repeatUnit),
                "MMMM d, yyyy"
              )}
            </div>
          </div>

          {repeatUnit === "week" && (
            <div className="space-y-2">
              <Label>Repeat on</Label>
              <div className="flex justify-between">
                {daysOfWeek.map((day, idx) => (
                  <Button
                    key={idx}
                    type="button"
                    variant={selectedDays.includes(idx) ? "default" : "outline"}
                    className="w-9 h-9 p-0"
                    onClick={() => {
                      setSelectedDays((prev) =>
                        prev.includes(idx)
                          ? prev.filter((d) => d !== idx)
                          : [...prev, idx]
                      );
                    }}
                  >
                    {day}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Ends</Label>
            <RadioGroup
              value={endType}
              onValueChange={(value) =>
                setEndType(value as "never" | "on" | "after")
              }
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="never" id="never" />
                <Label htmlFor="never">Never</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="on" id="on" />
                <Label htmlFor="on">On</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-[200px] pl-3 text-left font-normal text-sm",
                        endType !== "on" && "opacity-50"
                      )}
                      disabled={endType !== "on"}
                    >
                      {endDate ? format(endDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={(date) => date && setEndDate(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="after" id="after" />
                <Label htmlFor="after">After</Label>
                <div className="flex items-center space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={decrementOccurrences}
                    disabled={endType !== "after"}
                  >
                    <MinusCircle className="h-4 w-4" />
                  </Button>
                  <Input
                    type="text"
                    value={getOccurrencesText(occurrences)}
                    readOnly
                    className="w-[140px] text-center"
                    disabled={endType !== "after"}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={incrementOccurrences}
                    disabled={endType !== "after"}
                  >
                    <PlusCircle className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </RadioGroup>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleDone}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomRecurrenceDialog;
